import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Link,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  Campaign as CampaignIcon,
  Store as StoreIcon,
  ConfirmationNumber as CouponIcon,
  CheckCircle,
  Info,
  Person,
  Policy,
} from '@mui/icons-material';
import { couponService } from '../services/couponService';
import { campaignService } from '../services/campaignService';
import { storeService } from '../services/storeService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { AppHeader } from '../components/AppHeader';
import { Campaign, Store } from '../types';
import { translateStatus, getStatusColor } from '../utils/translations';

interface CouponFormData {
  number: number;
  campaignId: string;
  storeId: string;
  sellerName?: string;
  acceptTerms: boolean;
}

const couponSchema: yup.ObjectSchema<CouponFormData> = yup.object({
  number: yup.number()
    .required('Número do cupom é obrigatório')
    .min(1, 'Número deve ser maior que zero'),
  campaignId: yup.string().required('Campanha é obrigatória'),
  storeId: yup.string().required('Loja é obrigatória'),
  sellerName: yup.string().optional(),
  acceptTerms: yup.boolean()
    .oneOf([true], 'Você deve aceitar os termos e regulamento da promoção')
    .required('Aceitação dos termos é obrigatória')
});

const steps = ['Escolher Campanha', 'Selecionar Loja', 'Dados do Cupom', 'Confirmar'];

const CouponForm: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, logout } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [campaignStores, setCampaignStores] = useState<Store[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [activeStep, setActiveStep] = useState(0);

  const { control, handleSubmit, formState: { errors }, watch, setValue, trigger } = useForm<CouponFormData>({
    resolver: yupResolver(couponSchema),
    defaultValues: {
      number: 0,
      campaignId: '',
      storeId: '',
      sellerName: '',
      acceptTerms: false
    }
  });

  const watchedValues = watch();

  const fetchData = useCallback(async () => {
    try {
      setLoadingData(true);
      
      // Usuários finais veem apenas campanhas ativas
      const [campaignsData, storesData] = await Promise.all([
        campaignService.getActiveCampaigns(),
        storeService.getAllStores()
      ]);
      
      setCampaigns(campaignsData || []);
      setStores(storesData.stores || []);
    } catch (error: any) {
      console.error('❌ Erro ao carregar dados:', error);
      showError('Erro ao carregar campanhas e lojas. Tente novamente.');
    } finally {
      setLoadingData(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Atualizar campanha selecionada
  useEffect(() => {
    if (watchedValues.campaignId) {
      const campaign = campaigns.find(c => c.id === watchedValues.campaignId);
      setSelectedCampaign(campaign || null);
      
      // Filtrar lojas da campanha selecionada
      if (campaign?.stores) {
        setCampaignStores(campaign.stores);
      } else {
        setCampaignStores(stores);
      }
      
      // Reset store selection when campaign changes
      setValue('storeId', '');
      setSelectedStore(null);
    }
  }, [watchedValues.campaignId, campaigns, stores, setValue]);

  // Atualizar loja selecionada
  useEffect(() => {
    if (watchedValues.storeId) {
      const store = campaignStores.find(s => s.id === watchedValues.storeId);
      setSelectedStore(store || null);
    }
  }, [watchedValues.storeId, campaignStores]);

  const handleNext = async () => {
    let fieldsToValidate: (keyof CouponFormData)[] = [];
    
    switch (activeStep) {
      case 0:
        fieldsToValidate = ['campaignId'];
        break;
      case 1:
        fieldsToValidate = ['storeId'];
        break;
      case 2:
        fieldsToValidate = ['number'];
        break;
      case 3:
        fieldsToValidate = ['acceptTerms'];
        break;
    }
    
    const isStepValid = await trigger(fieldsToValidate);
    if (isStepValid) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onSubmit: SubmitHandler<CouponFormData> = async (data) => {
    try {
      setLoading(true);

      await couponService.createCoupon({
        number: data.number,
        campaignId: data.campaignId,
        storeId: data.storeId,
        sellerName: data.sellerName || undefined
      });

      showSuccess('Cupom cadastrado com sucesso! 🎉');
      navigate('/user/my-coupons');
    } catch (err: any) {
      console.error('❌ Erro ao salvar cupom:', err);
      
      if (err.response?.status === 409) {
        showError('Este número de cupom já foi cadastrado. Use um número diferente.');
      } else if (err.response?.status === 400) {
        // Mensagens específicas do backend para campanha pausada ou encerrada
        const errorMessage = err.response.data.message || 'Dados inválidos fornecidos';
        showError(errorMessage);
      } else if (err.response?.status === 404) {
        showError('Campanha ou loja não encontrada');
      } else if (err.response?.status === 403) {
        showError('Você não tem permissão para cadastrar cupons nesta campanha');
      } else {
        showError('Erro ao cadastrar cupom. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const isStepOptional = (step: number) => {
    return step === 2; // Apenas o passo do nome do vendedor é opcional
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CampaignIcon sx={{ mr: 1, color: 'primary.main' }} />
              Escolha uma Campanha
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Selecione a campanha promocional que você deseja participar
            </Typography>
            
            {campaigns.length === 0 ? (
              <Alert severity="info">
                Nenhuma campanha ativa encontrada no momento. Entre em contato com a associação.
              </Alert>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                {campaigns.map((campaign) => (
                  <Box key={campaign.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: watchedValues.campaignId === campaign.id ? 2 : 1,
                        borderColor: watchedValues.campaignId === campaign.id 
                          ? 'primary.main' 
                          : 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 2
                        }
                      }}
                      onClick={() => setValue('campaignId', campaign.id)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" gutterBottom>
                              {campaign.name}
                            </Typography>
                            {campaign.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {campaign.description}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip 
                                size="small" 
                                label={`${formatDate(campaign.startDate)} - ${formatDate(campaign.endDate)}`}
                                icon={<Info />}
                              />
                              <Chip 
                                size="small" 
                                label={translateStatus(campaign.status || 'ACTIVE')}
                                color={getStatusColor(campaign.status || 'ACTIVE')}
                              />
                            </Box>
                          </Box>
                          {watchedValues.campaignId === campaign.id && (
                            <CheckCircle color="primary" sx={{ ml: 2 }} />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <StoreIcon sx={{ mr: 1, color: 'primary.main' }} />
              Selecione a Loja
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Escolha a loja onde você fez a compra que dá direito ao cupom
            </Typography>

            {selectedCampaign && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Campanha selecionada:</strong> {selectedCampaign.name}
              </Alert>
            )}
            
            {campaignStores.length === 0 ? (
              <Alert severity="warning">
                Nenhuma loja participante encontrada para esta campanha.
              </Alert>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                {campaignStores.map((store) => (
                  <Box key={store.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: watchedValues.storeId === store.id ? 2 : 1,
                        borderColor: watchedValues.storeId === store.id 
                          ? 'primary.main' 
                          : 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 2
                        }
                      }}
                      onClick={() => setValue('storeId', store.id)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" gutterBottom>
                              {store.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              📍 {store.address}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              📞 {store.phone}
                            </Typography>
                          </Box>
                          {watchedValues.storeId === store.id && (
                            <CheckCircle color="primary" sx={{ ml: 2 }} />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CouponIcon sx={{ mr: 1, color: 'primary.main' }} />
              Dados do Cupom
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Informe os dados do seu cupom de participação
            </Typography>

            <Stack spacing={3}>
              <Controller
                name="number"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Número do Cupom"
                    type="number"
                    error={!!errors.number}
                    helperText={errors.number?.message || 'Digite o número do cupom que está na sua nota fiscal'}
                    inputProps={{ min: 1 }}
                    size="medium"
                    required
                  />
                )}
              />

              <Controller
                name="sellerName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Nome do Vendedor"
                    placeholder="Digite o nome do vendedor que atendeu você"
                    helperText="Campo opcional para identificar o vendedor"
                    InputProps={{
                      startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                )}
              />
            </Stack>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Policy sx={{ mr: 1, color: 'primary.main' }} />
              Confirmação e Termos
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Revise seus dados e aceite os termos da promoção
            </Typography>

            {/* Resumo dos dados */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                📋 Resumo do Cadastro
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Campanha:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedCampaign?.name || 'Não selecionada'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Loja:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedStore?.name || 'Não selecionada'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Número do Cupom:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {watchedValues.number || 'Não informado'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Vendedor:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {watchedValues.sellerName || 'Não informado'}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Regulamento da campanha */}
            {selectedCampaign?.regulation && (
              <Paper variant="outlined" sx={{ p: 3, mb: 3, maxHeight: 300, overflow: 'auto' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                  📜 Regulamento da Campanha
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {selectedCampaign.regulation}
                </Typography>
              </Paper>
            )}

            {/* Checkbox de aceite */}
            <Paper variant="outlined" sx={{ p: 3, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
              <Controller
                name="acceptTerms"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        color="primary"
                        size="large"
                      />
                    }
                    label={
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        ✅ Eu li e aceito os termos e regulamento da promoção e confirmo que todas as informações fornecidas são verdadeiras.
                        {selectedCampaign && (
                          <>
                            {' '}
                            <Link
                              href={`/campaigns/${selectedCampaign.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontWeight: 600 }}
                            >
                              Ver regulamento completo
                            </Link>
                          </>
                        )}
                      </Typography>
                    }
                  />
                )}
              />
              
              {errors.acceptTerms && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errors.acceptTerms.message}
                </Alert>
              )}
            </Paper>
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  if (loadingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Carregando campanhas...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <AppHeader title="Cadastrar Meu Cupom" subtitle="Participe das campanhas e concorra a prêmios incríveis" icon={<CouponIcon />} backTo="/user/my-coupons" />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              🎯 Cadastro de Cupom
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Participe das campanhas promocionais e concorra a prêmios incríveis!
            </Typography>
            
            {/* Informação destacada de quem está cadastrando */}
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                bgcolor: alpha(theme.palette.success.main, 0.1),
                border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                borderRadius: 2,
                maxWidth: 400,
                mx: 'auto'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Person sx={{ color: 'success.main' }} />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Cadastrando cupom para:
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main', mt: 0.5 }}>
                {user?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Este cupom será automaticamente vinculado à sua conta
              </Typography>
            </Paper>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label, index) => {
              const stepProps: { completed?: boolean } = {};
              const labelProps: { optional?: React.ReactNode } = {};
              
              if (isStepOptional(index)) {
                labelProps.optional = (
                  <Typography variant="caption">Opcional</Typography>
                );
              }
              
              return (
                <Step key={label} {...stepProps}>
                  <StepLabel {...labelProps}>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>

          {/* Step Content */}
          <Box sx={{ minHeight: '400px', mb: 4 }}>
            {renderStepContent(activeStep)}
          </Box>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
              variant="outlined"
            >
              Voltar
            </Button>
            
            <Box sx={{ flex: '1 1 auto' }} />
            
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit(onSubmit)}
                disabled={loading || !watchedValues.acceptTerms}
                size="large"
                sx={{ fontWeight: 600 }}
              >
                {loading ? 'Cadastrando...' : '🎉 Finalizar Cadastro'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={
                  (activeStep === 0 && !watchedValues.campaignId) ||
                  (activeStep === 1 && !watchedValues.storeId) ||
                  (activeStep === 2 && !watchedValues.number)
                }
                size="large"
                sx={{ fontWeight: 600 }}
              >
                Próximo
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </>
  );
}; 
export default CouponForm;